import { useState } from "react";
import { t } from "../../i18n";
import { GroupDialog } from "./GroupDialog";
import { groupLabel, groupUse, productsUsing } from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

/** On a card: at most this many categories, and per category this many products. */
const CARD_CATEGORIES = 5;
const CARD_PRODUCTS = 3;

/**
 * Keuzegroepen, as in the prototype: a group is made once and linked to many products; changing it
 * changes it everywhere at once.
 */
export function GroupsTab({ editor }: { editor: MenuEditor }) {
  const { menu, toast } = editor;
  const [open, setOpen] = useState<{ id: string | null } | null>(null);
  const text = t.menuAdmin.groups;

  return (
    <>
      <div className="menutop">
        <button
          type="button"
          className="btn"
          onClick={() => {
            setOpen({ id: null });
          }}
        >
          {text.add}
        </button>
        <span className="note">{text.note}</span>
      </div>
      <div className="grpcards">
        {Object.values(menu.modifierGroups).map((group) => {
          const used = productsUsing(menu, group.id).length;
          const names = group.items.map((item) => menu.products[item.productId]?.name ?? item.productId);
          const uses = groupUse(menu, group.id);
          return (
            <button
              key={group.id}
              type="button"
              className="btn grpcard"
              onClick={() => {
                setOpen({ id: group.id });
              }}
            >
              <span className="gct">
                <b>{group.title}</b>
                <span className="pill">{groupLabel(group)}</span>
              </span>
              <span className="note">
                {text.options(group.items.length)}
                {group.freeQuantity > 0 && text.free(group.freeQuantity)}
                {group.kind === "CHOICE"
                  ? text.choose(group.minimum, group.maximum)
                  : group.maximum > 0
                    ? text.most(group.maximum)
                    : ""}
                {group.items.some((item) => item.pricesBySize !== undefined) && text.bySize}
              </span>
              <span className="note">
                {names.slice(0, 6).join(", ")}
                {names.length > 6 ? "…" : ""}
              </span>
              <span className="gused">{text.usedBy(used)}</span>
              {/* where it is linked: a whole category, or which of its products */}
              <span className="guse" aria-label={text.linkedTo}>
                {uses.length === 0 && <span className="note">{text.linkedNone}</span>}
                {uses.slice(0, CARD_CATEGORIES).map((use) => (
                  <span key={use.category}>
                    <b>{use.category}</b>
                    <span>
                      {use.all
                        ? text.whole(use.of)
                        : text.some(use.products.slice(0, CARD_PRODUCTS), use.products.length - CARD_PRODUCTS)}
                    </span>
                  </span>
                ))}
                {uses.length > CARD_CATEGORIES && (
                  <span className="note">{text.moreCategories(uses.length - CARD_CATEGORIES)}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {open && (
        <GroupDialog
          editor={editor}
          group={open.id === null ? null : (menu.modifierGroups[open.id] ?? null)}
          onDone={(message) => {
            toast(message);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </>
  );
}
